/**
 * The assistant pipeline's stages (the stream's `status` events, e.g. `retrieve_evidence`) as phases a
 * user understands. Several internal steps make one phase — tracing, routing and dispatch mean nothing
 * to the reader — so consecutive steps of the same phase show as one line.
 *
 * `order` places a phase in the pipeline: answer text starts streaming before the `generate_answer`
 * step is reported, so text on screen means the writing phase has begun whatever the last step was.
 */

export interface AssistantPhase {
  id: string;
  labelKey: string;
  labelDefault: string;
  order: number;
}

const phase = (id: string, labelDefault: string, order: number): AssistantPhase => ({ id, labelKey: `Assistant.phase.${id}`, labelDefault, order });

const START = phase('start', 'Zahajuji zpracování', 0);
const UNDERSTAND = phase('understand', 'Rozebírám dotaz', 1);
const CONTEXT = phase('context', 'Procházím předchozí konverzaci', 2);
const SEARCH = phase('search', 'Hledám v návodech', 3);
const CLARIFY = phase('clarify', 'Ověřuji, zda je dotaz jasný', 4);
export const WRITING_PHASE = phase('write', 'Píšu odpověď', 5);
const SOURCES = phase('sources', 'Doplňuji zdroje', 6);
const FINISH = phase('finish', 'Dokončuji odpověď', 7);
/** Before the first step arrives, for a step this list does not know yet, and while polling. */
export const WORKING_PHASE = phase('working', 'Pracuji na odpovědi', -1);

const PHASE_BY_STEP: Record<string, AssistantPhase> = {
  start_trace: START,
  mark_request_running: START,
  classify_route: UNDERSTAND,
  dispatch_disposition: UNDERSTAND,
  load_context: CONTEXT,
  decide_retrieval: SEARCH,
  retrieve_evidence: SEARCH,
  decide_clarify: CLARIFY,
  generate_answer: WRITING_PHASE,
  build_citations: SOURCES,
  persist_answer: FINISH,
  trace_answer: FINISH,
};

/**
 * The phases so far, oldest first, each once — the last one is the phase in progress. `writing` says
 * answer text has already arrived, which puts the answer in the writing phase at least.
 */
export const phasesOf = (steps: string[] | undefined, writing: boolean): AssistantPhase[] => {
  const phases: AssistantPhase[] = [];
  (steps ?? []).forEach(step => {
    const next = PHASE_BY_STEP[step] ?? WORKING_PHASE;
    if (phases[phases.length - 1]?.id !== next.id) phases.push(next);
  });
  const current = phases[phases.length - 1];
  if (writing && (!current || current.order < WRITING_PHASE.order)) phases.push(WRITING_PHASE);
  return phases.length > 0 ? phases : [WORKING_PHASE];
};
